import { SkiPlan } from "../types";
import { Plus, Edit, Trash2, Calendar, MapPin, Mountain, Globe, Lock, Eye } from "lucide-react";

interface PlanListProps {
    plans: SkiPlan[];
    onCreateNew: () => void;
    onEdit: (plan: SkiPlan) => void;
    onDelete: (localId: string) => void; // ✅ local id ONLY
}

type Badge = {
    label: string;
    icon: any; // lucide icon component
    className: string;
    title: string;
};

export function PlanList({ plans, onCreateNew, onEdit, onDelete }: PlanListProps) {
    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <h2>Plans de Course</h2>
                </div>

                <button
                    onClick={onCreateNew}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    Nouveau Plan
                </button>
            </div>

            {plans.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <Mountain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Aucun plan de course créé</p>
                    <button onClick={onCreateNew} className="text-blue-600 hover:text-blue-700">
                        Créer votre premier plan
                    </button>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {plans.map((plan) => {
                        const id = plan.id; // ✅ FIX: utilisé pour key + delete

                        const hasCloud = !!plan.cloudId;
                        const isPlanPublic = !!plan.isPublic;
                        const isPlanReadOnlyMode = !!plan.isReadOnly;

                        // ✅ 1 seul badge max
                        const badge: Badge = !hasCloud
                            ? {
                                label: "Local",
                                icon: Eye,
                                className: "bg-gray-200 text-gray-700",
                                title: "Plan local (non synchronisé)",
                            }
                            : !isPlanPublic
                                ? {
                                    label: "Privé",
                                    icon: Lock,
                                    className: "bg-gray-800 text-white",
                                    title: "Plan privé (cloud)",
                                }
                                : isPlanReadOnlyMode
                                    ? {
                                        label: "Public — RO",
                                        icon: Lock,
                                        // ✅ RO plus subtil que "Public"
                                        className: "bg-amber-100 text-amber-800 border border-amber-200",
                                        title: "Public — lecture seule",
                                    }
                                    : {
                                        label: "Public",
                                        icon: Globe,
                                        className: "bg-green-600 text-white",
                                        title: "Public — collaboratif",
                                    };

                        const Icon = badge.icon;

                        return (
                            <div
                                key={id}
                                className="bg-white rounded-lg border overflow-hidden hover:shadow-lg transition-all border-gray-200"
                            >
                                {/* Image + badges */}
                                <div className="relative">
                                    {plan.thumbnail || plan.siteImage ? (
                                        <div className="h-48 bg-gray-100 relative overflow-hidden">
                                            <img
                                                src={(plan.thumbnail || plan.siteImage)!}
                                                alt={plan.name || "Plan"}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ) : (
                                        <div className="h-48 bg-gray-100 flex items-center justify-center">
                                            <Mountain className="w-16 h-16 text-gray-300" />
                                        </div>
                                    )}

                                    <div className="absolute top-2 left-2">
                                        <div
                                            className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${badge.className}`}
                                            title={badge.title}
                                        >
                                            <Icon className="w-3 h-3" />
                                            {badge.label}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6">
                                    <h3 className="mb-2">{plan.name || "Sans titre"}</h3>

                                    {plan.raceType && (
                                        <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm mb-3">
                                            {plan.raceType}
                                        </span>
                                    )}

                                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                                        {plan.location && (
                                            <div className="flex items-center gap-2">
                                                <MapPin className="w-4 h-4" />
                                                <span>{plan.location}</span>
                                            </div>
                                        )}

                                        {plan.date && (
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4" />
                                                <span>{new Date(plan.date).toLocaleDateString("fr-FR")}</span>
                                            </div>
                                        )}

                                        {plan.totalDistance && (
                                            <div className="flex items-center gap-2">
                                                <Mountain className="w-4 h-4" />
                                                <span>
                                                    {plan.totalDistance}m{" "}
                                                    {plan.numLaps && plan.numLaps > 1 ? `(${plan.numLaps} tours)` : ""}
                                                </span>
                                            </div>
                                        )}

                                        {plan.technique && (
                                            <div className="text-xs">
                                                <span className="px-2 py-1 bg-gray-100 rounded">
                                                    Technique: {plan.technique}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 pt-4 border-t">
                                        <button
                                            onClick={() => onEdit(plan)}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                        >
                                            <Edit className="w-4 h-4" />
                                            Éditer
                                        </button>

                                        <button
                                            onClick={() => {
                                                if (confirm("Êtes-vous sûr de vouloir supprimer ce plan ?")) {
                                                    onDelete(id); // ✅ local id only
                                                }
                                            }}
                                            className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}